import { motion } from "motion/react";
import SectionSepratorHeader from "../../components/ui/section-seprator-header";
import Noise from "../../components/core/noise";

const TECHNICAL_DETAILS = [
  {
    title: "Structural Integrity & Engineering",
    description:
      "Every signage project at Metal FX begins with rigorous structural calculations. We don't just build for aesthetics; we engineer for environmental resilience. Our systems are designed to withstand high wind velocities, seismic variations, and structural load shifts common in commercial architecture.",
    image:
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=2070&auto=format&fit=crop",
    points: [
      "Wind-load assessment for high-rise installations",
      "Seismic-rated mounting and substructures",
      "Dynamic load testing for cantilevered designs",
      "PE-stamped engineering drawings for city permits",
    ],
    technicalSpec: "ASTM A36 Steel / 6061-T6 Aluminum",
  },
  {
    title: "Material Science & Durability",
    description:
      "Selection of materials is the foundation of longevity. We utilize aircraft-grade aluminum, marine-grade stainless steel, and high-density, UV-stabilized polymers. This ensures that your brand asset remains vibrant and structurally sound for decades, rather than years.",
    image:
      "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop",
    points: [
      "6061-T6 structural aluminum for weight efficiency",
      "316 marine-grade steel for coastal corrosion resistance",
      "Automotive-spec paint systems with clear-coat protection",
      "Precision CNC fabrication for zero-tolerance fitting",
    ],
    technicalSpec: "ISO 9001:2015 Certified Fabrication",
  },
  {
    title: "Illumination & Photometrics",
    description:
      "Light isn't just about visibility; it's about atmosphere and consistency. Our illumination engineers calculate the exact lumen output and color temperature (Kelvin) required to maintain brand fidelity across any lighting condition, using only top-tier, binned LED technology.",
    image:
      "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=2070&auto=format&fit=crop",
    points: [
      "Calculated light distribution for zero hot-spots",
      "24V efficiency-regulated electrical systems",
      "Advanced thermal management for LED longevity",
      "Smart-control integration for automated dimming",
    ],
    technicalSpec: "UL 48 / CSA C22.2 Compliant",
  },
  {
    title: "Compliance & Safety Standards",
    description:
      "Navigating the complexities of UL certification, ADA requirements, and local zoning ordinances is integral to our service. We ensure that your signage is not only impactful but fully compliant with every jurisdictional safety and accessibility standard.",
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=2084&auto=format&fit=crop",
    points: [
      "UL-73 compliance for all electrical components",
      "ADA-compliant tactile and Braille integration",
      "Zoning-compliant visibility and height analysis",
      "Sustainable fabrication reducing carbon footprint",
    ],
    technicalSpec: "IBC 2021 / ADA / NFPA 701",
  },
];

const TechnicalCard = ({
  detail,
  index,
}: {
  detail: (typeof TECHNICAL_DETAILS)[0];
  index: number;
}) => {
  const isEven = index % 2 === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.8,
        delay: index * 0.1,
        ease: [0.16, 1, 0.3, 1],
      }}
      viewport={{ once: true, margin: "-100px" }}
      className="relative w-full group"
    >
      <div className="relative overflow-hidden rounded-4xl bg-[#1a1c1c]/30 border border-white/5 backdrop-blur-sm shadow-2xl transition-all duration-700 hover:border-[#4c94ba]/40">
        <Noise />
        <div
          className={`flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} items-stretch min-h-[500px]`}
        >
          {/* Image Section */}
          <div className="w-full lg:w-2/5 relative overflow-hidden h-[300px] lg:h-auto">
            <img
              src={detail.image}
              alt={detail.title}
              className="w-full h-full object-cover grayscale brightness-75 group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000"
            />
            <div className="absolute inset-0 bg-linear-to-t from-[#111212] lg:hidden" />

            {/* Blueprint Crosshair Overlay */}
            <div className="absolute inset-0 z-10 p-6 pointer-events-none opacity-40">
              <div className="absolute top-8 left-8 w-12 h-px bg-[#4c94ba]" />
              <div className="absolute top-8 left-8 w-px h-12 bg-[#4c94ba]" />
              <div className="absolute top-1/2 left-8 -translate-y-1/2 flex flex-col items-center gap-1">
                <div className="w-1 h-1 bg-[#4c94ba] rounded-full" />
                <div className="w-px h-10 bg-[#4c94ba]/30" />
                <div className="w-1 h-1 bg-[#4c94ba] rounded-full" />
              </div>
            </div>

            {/* Tech Tag */}
            <div className="absolute bottom-6 left-6 z-20 backdrop-blur-md bg-black/40 border border-white/10 px-4 py-2 rounded-full hidden lg:block">
              <span className="text-[10px] text-[#4c94ba] font-bold tracking-widest uppercase">
                {detail.technicalSpec}
              </span>
            </div>
          </div>

          {/* Content Section */}
          <div className="w-full lg:w-3/5 p-8 md:p-12 lg:p-16 flex flex-col relative">
            {/* Top decorative line */}
            <div
              className={`absolute top-0 ${isEven ? "left-0" : "right-0"} w-1/2 h-px bg-linear-to-r from-[#4c94ba]/0 via-[#4c94ba]/40 to-[#4c94ba]/0`}
            />

            <div className="mb-8">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-4xl font-black text-[#4c94ba]/20 leading-none">
                  0{index + 1}
                </span>
                <div className="w-12 h-px bg-[#4c94ba]/20" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-tight group-hover:text-[#4c94ba] transition-colors">
                {detail.title}
              </h3>
              <p className="text-zinc-400 text-lg leading-relaxed font-medium">
                {detail.description}
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 relative">
              {detail.points.map((point, pIdx) => (
                <div key={pIdx} className="flex items-start gap-4 group/item">
                  <div className="w-5 h-5 rounded-sm border border-[#4c94ba]/30 flex items-center justify-center mt-1 group-hover/item:border-[#4c94ba] transition-colors">
                    <div className="w-2 h-2 bg-[#4c94ba] scale-0 group-hover/item:scale-100 transition-transform" />
                  </div>
                  <span className="text-zinc-500 text-sm font-semibold tracking-wide flex-1 leading-snug group-hover/item:text-zinc-300 transition-colors">
                    {point}
                  </span>
                </div>
              ))}

              {/* Corner crosshairs for list area */}
              <div className="absolute -bottom-4 -right-4 w-6 h-6 border-b border-r border-[#4c94ba]/20 hidden md:block" />
            </div>

            {/* Mobile Tech Tag */}
            <div className="mt-10 lg:hidden self-start backdrop-blur-md bg-white/5 border border-white/10 px-4 py-2 rounded-full">
              <span className="text-[10px] text-[#4c94ba] font-bold tracking-widest uppercase">
                {detail.technicalSpec}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const TechnicalExcellence = () => {
  return (
    <section className="w-full bg-[#111212] mt-24 relative overflow-hidden">
      <div className="mx-auto px-2.5 md:px-6 ">
        <div className="relative mb-20">
          <SectionSepratorHeader
            title="TECHNICAL EXCELLENCE"
            subtitle={
              <>
                The Architectural <br /> Science of Signage.
              </>
            }
            description="Moving beyond simple fabrication, Metal FX bridges the gap between industrial engineering and high-end brand aesthetics. Explore our technical standards for permanent commercial installations."
            className="px-0! mb-0!"
          />

          {/* Decorative background circle */}
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-[#4c94ba]/10 blur-[120px] rounded-full pointer-events-none" />
        </div>
        <div className="flex flex-col gap-12 lg:gap-24 relative">
          <div className="absolute inset-0 pointer-events-none opacity-[0.3]">
            <div className="absolute left-1/2 top-0 w-px h-full bg-[#4c94ba] hidden lg:block" />
            {TECHNICAL_DETAILS.map((_, i) => (
              <div
                key={i}
                className="w-full h-px bg-[#4c94ba] absolute"
                style={{ top: `${(i + 1) * 25}%` }}
              />
            ))}
          </div>
          {TECHNICAL_DETAILS.map((detail, index) => (
            <TechnicalCard key={index} detail={detail} index={index} />
          ))}
        </div>
        <div className="mt-7 flex flex-col items-center justify-center opacity-20">
          <div className="w-px h-24 bg-linear-to-b from-[#4c94ba] to-transparent" />
          <span className="text-[10px] text-zinc-400 tracking-[1em] uppercase mt-4">
            END SPECIFICATION
          </span>
        </div>
      </div>
    </section>
  );
};

export default TechnicalExcellence;
